import _ from "lodash";
import React, { useMemo, useState, useCallback } from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { IconButton, Modal, Tag, Tooltip } from "@toddle-edu/ds-web";
import {
  ArrowOutOutlined,
  CopyOutlined,
  InformationFilled,
  ImportOutlined,
} from "@toddle-edu/ds-icons";
import I18nHOC from "UIComponents/I18nHOC";
import Accordian from "UIComponents/Accordian";
import MarkdownView from "UIComponents/MarkdownView";
import Loading from "UIComponents/Loading";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { compose, graphql } from "lib/apolloClient";
import client from "lib/apolloClient/client";
import { AI_USER_ROLES_INFO, openLinkInNewTab } from "modules/Services";
import { setToastMsg, getUserInfo } from "Login/modules/LoginModule";
import { copyToClipboard } from "Utils/CopyToClipboard";
import { getAiUsageMetricsQuery } from "../../modules/AIFeedbackStripQueries";
import { getAiUsageMetricsQueryFromCache } from "../../modules/AIFeedbackStripGraphqlHelpers";
import { getAIConversationsQuery } from "modules/CommonQuery";
import { getAIConversationsFromCache } from "modules/CommonGraphqlHelpers";
import { getUserDetailsWithLinkedIdentitiesQuery } from "Tooling/routes/AccessManagerV2/modules/AccessManagerV2Queries";
import { transformAiUsageMetricsToPlayground } from "routes/routes/Teacher/routes/AIPlayground/modules/AIPlaygroundUtils";
import ACLStore from "lib/aclStore";
import { aiPlaygroundOrgId, langfuse } from "config";
import classes from "./AIDetailedPromptModal.scss";
import { colors } from "Constants";
import {
  getCommonMarkdownComponents,
  getLangfuseTraceUrl,
} from "Utils/AIUtils";

const styles = {
  headerStyle: {
    padding: "16px 2px",
    width: "100%",
  },
  headerLabelStyle: {
    width: "100%",
  },
  modalBodyStyle: {
    padding: "0px",
  },
  markdownContainer: {
    overflow: "visible",
  },
  timeLabelStyle: {
    width: "100%",
    fontSize: 12,
    textAlign: "right",
    color: colors.textSubtle,
    marginRight: 4,
  },
};

const getUpdatedContent = ({ content, sourceFrom }) => {
  if (_.isEmpty(content)) return "";

  switch (sourceFrom) {
    // for unit planning, we do not need to replace heading
    case "UNIT_PLANNING_ASSISTANT":
      return content;

    default: {
      let parsedContent = _.trimStart(content, "```xml", "");
      parsedContent = _.trimEnd(parsedContent, "```", "");
      return _.replace(parsedContent, /###/g, "-----");
    }
  }
};

const AI_PLAYGROUND_ORG_ID = aiPlaygroundOrgId;
const TODDLE_SUPPORT_EMAIL_PREFIX = "todden.superteacher";

const AIDetailedPromptModal = props => {
  const {
    t,
    aiUsageMetrics,
    isLoading,
    sourceFrom,
    setToastMsg,
    openLinkInNewTab,
    onModalClose,
    model,
    temperature,
    usageMetricsId,
    testId,
    messageLimit,
  } = props;
  const { inputText, responseText } = aiUsageMetrics || {};

  const [nestedToolMetricId, setNestedToolMetricId] = useState(null);
  const [isImportLoading, setIsImportLoading] = useState(false);

  const isAIDebugModeEnabled =
    ACLStore.can("IdentityAccess:EnableAIDebugMode") ||
    ACLStore.can("Common:EnableAIDebugMode");
  const traceId = _.get(aiUsageMetrics, "metadata.traceId", "");
  const observationId = _.get(aiUsageMetrics, "metadata.observationId", "");
  const langfuseDetails = _.get(
    langfuse,
    `${_.get(getUserInfo(), "orgRegion", "")}`,
    {}
  );
  const traceUrl = getLangfuseTraceUrl({
    traceId,
    observationId,
    langfuseDetails,
  });

  const handleOpenInAIPlayground = useCallback(
    async metricsId => {
      try {
        setIsImportLoading(true);
        const userInfo = getUserInfo();
        const userId = _.get(userInfo, "id");
        const userEntityType = _.get(userInfo, "userEntityType");

        if (!userId || !userEntityType) {
          setToastMsg({
            msg: "Unable to get user information. Please try again.",
            type: "error",
            notString: true,
          });
          setIsImportLoading(false);
          return;
        }

        const linkedUsersResponse = await client.query({
          query: getUserDetailsWithLinkedIdentitiesQuery,
          variables: {
            id: userId,
            type: userEntityType,
            first: 100,
          },
          fetchPolicy: "network-only",
        });

        const linkedUsersList = _.map(
          _.get(
            linkedUsersResponse,
            "data.node.linkedIdentityUsersV2.edgesV2",
            []
          ),
          "node"
        );

        const selectedIdentityDetails = _.find(
          linkedUsersList,
          linkedUser =>
            _.get(linkedUser, "organizationV2.id") === AI_PLAYGROUND_ORG_ID &&
            !_.startsWith(
              _.get(linkedUser, "email", ""),
              TODDLE_SUPPORT_EMAIL_PREFIX
            )
        );

        if (!selectedIdentityDetails) {
          setToastMsg({
            msg:
              "You don't have access to AI Playground org. Please contact admin.",
            type: "error",
            notString: true,
          });
          setIsImportLoading(false);
          return;
        }

        const usageMetricsResponse = await client.query({
          query: getAiUsageMetricsQuery,
          variables: { id: metricsId },
          fetchPolicy: "network-only",
        });
        const fetchedUsageMetrics = _.get(
          usageMetricsResponse,
          "data.node",
          {}
        );

        if (_.isEmpty(fetchedUsageMetrics)) {
          setToastMsg({
            msg: "Unable to fetch usage metrics data.",
            type: "error",
            notString: true,
          });
          setIsImportLoading(false);
          return;
        }

        const transformedData = transformAiUsageMetricsToPlayground(
          fetchedUsageMetrics
        );

        if (!transformedData) {
          setToastMsg({
            msg: "Unable to transform usage metrics data.",
            type: "error",
            notString: true,
          });
          setIsImportLoading(false);
          return;
        }

        localStorage.setItem(
          "aiPlayground_pendingImportData",
          JSON.stringify({
            usageMetricsId: metricsId,
            entityType: _.get(fetchedUsageMetrics, "entityType", ""),
            conversationDetails: transformedData.conversationDetails,
            messages: transformedData.messages,
          })
        );

        // Store org switch data for the new tab to pick up
        const orgSwitchData = {
          id: _.get(selectedIdentityDetails, "id", ""),
          identityId: _.get(selectedIdentityDetails, "identityId", ""),
          jwt: _.get(selectedIdentityDetails, "jwt", ""),
          type: _.get(selectedIdentityDetails, "type", ""),
          organization: _.get(selectedIdentityDetails, "organizationV2", {}),
          designation: _.get(selectedIdentityDetails, "designation", {}),
        };
        localStorage.setItem(
          "aiPlayground_pendingOrgSwitch",
          JSON.stringify(orgSwitchData)
        );

        setToastMsg({
          msg: "Opening AI Playground in a new tab...",
          type: "success",
          notString: true,
        });

        // Open new tab - it will pick up the pending org switch from localStorage
        openLinkInNewTab({ url: "/" });

        setIsImportLoading(false);
      } catch (error) {
        console.error("Error opening AI Playground:", error);
        setToastMsg({
          msg: "Failed to open AI Playground. Please try again.",
          type: "error",
          notString: true,
        });
        setIsImportLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setToastMsg, openLinkInNewTab]
  );

  const markdownViewClasses = classNames(
    classes.assessmentToolMarkdown,
    "text-body-2"
  );

  const onCopy = content => {
    copyToClipboard(content, () => {
      setToastMsg({
        msg: "toastMsgs:copied_capital",
        type: "success",
      });
    });
  };

  const shouldShowMarkdownView = _.includes(
    ["UNIT_PLANNING_ASSISTANT", "LESSON_PLANNING_ASSISTANT", "STUDENT_PREVIEW"],
    sourceFrom
  );

  const handleOpenToolMetric = metricId => {
    setNestedToolMetricId(metricId);
  };

  const handleCloseNestedModal = () => {
    setNestedToolMetricId(null);
  };

  const getTooltipContent = () => {
    const promptTokens = _.get(aiUsageMetrics, "promptTokens");
    const completionTokens = _.get(aiUsageMetrics, "completionTokens");
    const cachedPromptTokens = _.get(aiUsageMetrics, "cachedPromptTokens");
    const metadata = _.get(aiUsageMetrics, "metadata");

    const totalRequestTime = `${_.get(
      metadata,
      "totalRequestTime",
      "0"
    )} seconds`;

    const tooltipContent = `Total Request Time: ${totalRequestTime} <br/> <hr/> Prompt Tokens: ${
      promptTokens || 0
    } <br/> Output Tokens: ${
      completionTokens || 0
    } <br/> Cached Prompt Tokens: ${cachedPromptTokens || 0}`;

    return tooltipContent;
  };

  const markdownComponents = getCommonMarkdownComponents();

  const messagesToShow = useMemo(() => {
    const messages = inputText?.messages || [];
    if (!messageLimit || messages.length <= messageLimit) {
      return messages;
    }
    // take system message and last N messages
    return [_.nth(messages, 0), ..._.takeRight(messages, messageLimit)];
  }, [inputText, messageLimit]);

  return (
    <>
      <Modal width="80vw" height="80vh" onClose={onModalClose}>
        <div className="activeDuringPrompt">
          <Modal.Header hasSeparator={true}>
            <div className={classes.headerContainer}>
              <div className={classes.headerLeftContainer}>
                <div>{t("common:detailed_prompt")}</div>
              </div>
              <div className={classes.headerRightContainer}>
                {!_.isNil(model) && (
                  <Tag _legacy_size="small" size="medium">{`${t(
                    "ai:model"
                  )}: ${model}`}</Tag>
                )}
                {!_.isNil(temperature) && (
                  <Tag _legacy_size="small" size="medium">{`${t(
                    "ai:temperature"
                  )}: ${temperature}`}</Tag>
                )}
                {!_.isNil(usageMetricsId) && (
                  <>
                    <Tag _legacy_size="small" size="medium">
                      Usage metric ID : {usageMetricsId}
                    </Tag>
                    <IconButton
                      size={"small"}
                      icon={<CopyOutlined variant="subtle" />}
                      _legacy_variant={"outlined-subtle"}
                      variant="neutral"
                      type="outlined"
                      onClick={() => onCopy(usageMetricsId)}
                      testId={`${testId}-copyUsageMetricsId`}
                    />
                    {isAIDebugModeEnabled && (
                      <Tooltip tooltip={"Import to Playground"}>
                        <IconButton
                          size={"small"}
                          icon={<ImportOutlined variant="link" />}
                          _legacy_variant={"outlined-subtle"}
                          variant="progressive"
                          type="outlined"
                          onClick={() =>
                            handleOpenInAIPlayground(usageMetricsId)
                          }
                          testId={`${testId}-importToPlayground`}
                        />
                      </Tooltip>
                    )}
                    {!_.isEmpty(traceUrl) && (
                      <Tooltip tooltip={"Open in Langfuse"}>
                        <IconButton
                          size={"small"}
                          icon={<ArrowOutOutlined variant="link" />}
                          _legacy_variant={"outlined-subtle"}
                          variant="progressive"
                          type="outlined"
                          onClick={() => openLinkInNewTab({ url: traceUrl })}
                          testId={`${testId}-openInLangfuse`}
                        />
                      </Tooltip>
                    )}
                    <Tooltip tooltip={getTooltipContent()}>
                      <InformationFilled size="small" variant="link" />
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
          </Modal.Header>
        </div>
        <Modal.Body isScrollable={true} style={styles.modalBodyStyle}>
          {isLoading ? (
            <Loading />
          ) : (
            <div
              className={classNames(
                classes.bodyContainer,
                "activeDuringPrompt"
              )}
            >
              <div className={classes.leftContainer}>
                {responseText?.reasoning ? (
                  <>
                    <div className="text-label-2">{"Reasoning"}</div>
                    <MarkdownView
                      content={getUpdatedContent({
                        content: responseText?.reasoning,
                        sourceFrom,
                      })}
                      containerStyle={styles.markdownContainer}
                      contentContainerClass={markdownViewClasses}
                      components={markdownComponents}
                      testId={`${testId}-markdownView-reasoning`}
                    />
                  </>
                ) : null}
                {!_.isEmpty(_.get(props, "miscInfo", "")) ? (
                  <Accordian
                    title={"Miscellaneous Info (Not Part of AI output)"}
                    expandedContent={
                      <MarkdownView
                        content={getUpdatedContent({
                          content: _.get(props, "miscInfo", ""),
                          sourceFrom,
                        })}
                        testId={`${testId}-miscInfo-markdownView`}
                      />
                    }
                  />
                ) : null}
                <div className="text-label-2">{t("common:final_output")}</div>
                <MarkdownView
                  content={getUpdatedContent({
                    content: responseText?.content,
                    sourceFrom,
                  })}
                  containerStyle={styles.markdownContainer}
                  contentContainerClass={markdownViewClasses}
                  components={markdownComponents}
                  testId={`${testId}-markdownView-finalOutput`}
                />
              </div>
              <div className={classes.rightContainer}>
                {_.map(messagesToShow, (message, index) => {
                  const { role, content, tool_calls, usageMetrics } = message;
                  const messageUsageMetricsId = _.get(
                    usageMetrics,
                    "id",
                    _.get(message, "usageMetricsId", null)
                  );
                  const roleInfo = AI_USER_ROLES_INFO[role];
                  const isToolRole = role === "tool";
                  const isAssistantRole = role === "assistant";
                  const hasToolMetricId = !_.isNil(messageUsageMetricsId);

                  let updatedContent = content;
                  if (!_.isString(updatedContent)) {
                    updatedContent = _.map(updatedContent, item => {
                      switch (item.type) {
                        case "text":
                          return item.text;
                        case "image_url": {
                          return item?.image_url?.url;
                        }
                        case "audio_url":
                          return item?.audio_url?.url;
                        default:
                          return "";
                      }
                    })
                      .filter(Boolean)
                      .join("\n\n");
                  }

                  return (
                    <Accordian
                      key={`${role}-${index}`}
                      title={
                        <div className={classes.accordionTitleContainer}>
                          <span style={{ color: roleInfo?.color }}>
                            {t(roleInfo?.labelLocale)}
                          </span>
                          {isToolRole &&
                          !_.isNil(
                            _.get(
                              message,
                              "usageMetrics.totalToolExecutionTime",
                              _.get(
                                message,
                                "usageMetrics.totalAgentExecutionTime",
                                null
                              )
                            )
                          ) ? (
                            <b style={styles.timeLabelStyle}>
                              {_.get(
                                message,
                                "usageMetrics.totalToolExecutionTime",
                                _.get(
                                  message,
                                  "usageMetrics.totalAgentExecutionTime",
                                  "0"
                                )
                              )}{" "}
                              seconds
                            </b>
                          ) : null}
                          {isAssistantRole &&
                          !_.isNil(_.get(message, "totalChunkTime", null)) ? (
                            <b style={styles.timeLabelStyle}>
                              {_.get(message, "totalChunkTime", 0)} Seconds
                            </b>
                          ) : null}
                        </div>
                      }
                      headerStyle={styles.headerStyle}
                      customTitleStyle={styles.headerLabelStyle}
                      hideContainerBorder={true}
                      showLabelBorder={true}
                      isRightArrowChervon={true}
                      expandedContent={
                        <div className={classes.expandedContentMainContainer}>
                          <div className={classes.expandedContentCon}>
                            <div
                              style={{
                                maxWidth: "90%",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {_.map(
                                tool_calls,
                                (
                                  { function: { name, arguments: args } },
                                  index
                                ) =>
                                  `${index + 1}. ${t(
                                    "common:name"
                                  )}: ${name}\n${t(
                                    "common:description"
                                  )}: ${args}\n\n`
                              )}
                            </div>
                            {shouldShowMarkdownView ? (
                              <MarkdownView
                                content={getUpdatedContent({
                                  content: updatedContent,
                                  sourceFrom,
                                })}
                                containerStyle={styles.markdownContainer}
                                contentContainerClass={markdownViewClasses}
                                components={markdownComponents}
                                testId={`${testId}-markdownView`}
                              />
                            ) : (
                              <div className={classes.contentContainer}>
                                {updatedContent}
                              </div>
                            )}
                            {!_.isEmpty(_.trim(updatedContent)) && (
                              <div className={classes.buttonContainer}>
                                {isToolRole && hasToolMetricId && (
                                  <IconButton
                                    size={"small"}
                                    icon={<ArrowOutOutlined />}
                                    variant={"progressive"}
                                    type={"fill"}
                                    onClick={() =>
                                      handleOpenToolMetric(
                                        messageUsageMetricsId
                                      )
                                    }
                                    testId={`${testId}-openToolMetric-${index}`}
                                  />
                                )}
                                <IconButton
                                  size={"small"}
                                  icon={<CopyOutlined variant="subtle" />}
                                  _legacy_variant={"outlined-subtle"}
                                  variant="neutral"
                                  type="outlined"
                                  onClick={() => onCopy(updatedContent)}
                                  testId={`${testId}-copyPrompt`}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      }
                    />
                  );
                })}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
      {nestedToolMetricId && (
        <WrappedAIDetailedPromptModal
          key={nestedToolMetricId}
          t={t}
          aiConversationId={null}
          usageMetricsId={nestedToolMetricId}
          onModalClose={handleCloseNestedModal}
          sourceFrom={sourceFrom}
          temperature={temperature}
          model={model}
        />
      )}
      {isImportLoading && (
        <FullScreenLoader overlayContainer={classes.overlayContainer} />
      )}
    </>
  );
};

const mapActionCreators = {
  setToastMsg,
  openLinkInNewTab,
};

const WrappedAIDetailedPromptModal = compose(
  I18nHOC({ resource: ["ai", "common"] }),
  connect(null, mapActionCreators),
  graphql(getAIConversationsQuery, {
    name: "getAIConversations",
    skip: ({ aiConversationId, usageMetricsId }) =>
      !_.isEmpty(usageMetricsId) || _.isEmpty(aiConversationId),
    options: ({ aiConversationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: aiConversationId, portalType: "PLANNER" },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getAIConversations,
      ownProps: { isData, isLoading, infoKey = "debugInfo" },
    }) {
      const queryData = getAIConversationsFromCache(
        getAIConversations.variables
      );
      const networkStatus = _.get(getAIConversations, "networkStatus");
      const usageMetricsId = _.get(
        queryData,
        ["metadata", infoKey, "usageMetricsId"],
        ""
      );

      return {
        isFetchingMoreData: networkStatus == 3,
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        usageMetricsId,
      };
    },
  }),
  graphql(getAiUsageMetricsQuery, {
    name: "getAiUsageMetrics",
    skip: ({ usageMetricsId }) => !usageMetricsId,
    options: ({ usageMetricsId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: usageMetricsId,
      },
    }),
    props({
      getAiUsageMetrics,
      ownProps: { usageMetricsId, isData, isLoading },
    }) {
      const aiUsageMetrics = getAiUsageMetricsQueryFromCache({
        id: usageMetricsId,
      });

      const model = _.get(aiUsageMetrics, "model");
      const temperature = _.get(aiUsageMetrics, "temperature");

      return {
        aiUsageMetrics,
        model,
        temperature,
        isData: !_.isEmpty(aiUsageMetrics) && isData,
        isLoading:
          _.includes([1, 2], getAiUsageMetrics.networkStatus) || isLoading,
      };
    },
  })
)(AIDetailedPromptModal);

export default WrappedAIDetailedPromptModal;

AIDetailedPromptModal.defaultProps = {
  testId: "appComponent-aiDetailPrompt-modal",
};

// ensure wrapped component has same default props
WrappedAIDetailedPromptModal.defaultProps = AIDetailedPromptModal.defaultProps;
